import React, { useState, useEffect, useMemo } from "react";
import { Table } from "react-bootstrap";
import { getProductos, deleteProducto } from "../helpers/rutaProductos";
import { puedeVerCostosFront } from "../helpers/permisosCostos";
import { getCategoriasProductos } from "../helpers/rutaCategoriasProductos";
import { getMarcas } from "../helpers/rutaMarcas";
import { getColores } from "../helpers/rutaColores";
import ModalProducto from "./ModalProducto";
import "../css/tableclientes.css";

/* ---------------- utils ---------------- */
const clean = (v) => String(v ?? "").trim();

const formatUSD = (v) => {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "USD 0.00";

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "object") return clean(v._id ?? v.id ?? v.value ?? "");
  return clean(v);
};

/* ---------------- normalizadores ---------------- */
const unwrapProductos = (r) => {
  if (Array.isArray(r?.productos)) return r.productos;
  if (Array.isArray(r?.data?.productos)) return r.data.productos;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const unwrapCategorias = (r) => {
  if (Array.isArray(r?.categoriasProductos)) return r.categoriasProductos;
  if (Array.isArray(r?.categorias)) return r.categorias;
  if (Array.isArray(r?.data?.categoriasProductos)) return r.data.categoriasProductos;
  if (Array.isArray(r?.data?.categorias)) return r.data.categorias;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const unwrapMarcas = (r) => {
  if (Array.isArray(r?.marcas)) return r.marcas;
  if (Array.isArray(r?.data?.marcas)) return r.data.marcas;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

const unwrapColores = (r) => {
  if (Array.isArray(r?.colores)) return r.colores;
  if (Array.isArray(r?.data?.colores)) return r.data.colores;
  if (Array.isArray(r?.data)) return r.data;
  if (Array.isArray(r)) return r;
  return [];
};

/* ---------------- nombres ---------------- */
const getNombreCategoria = (c) =>
  clean(c?.nombre) ||
  clean(c?.categoria) ||
  clean(c?.descripcion) ||
  clean(c?.label) ||
  "-";

const getCodigoCategoria = (c) =>
  clean(c?.codigo) ||
  clean(c?.codcategoria) ||
  clean(c?.codcat) ||
  clean(c?.cod) ||
  "";

const getNombreMarca = (m) =>
  clean(m?.marca) ||
  clean(m?.nombre) ||
  clean(m?.descripcion) ||
  clean(m?.label) ||
  "-";

const getCodigoMarca = (m) =>
  clean(m?.codmarca) ||
  clean(m?.codigo) ||
  clean(m?.cod) ||
  "";

const getNombreColor = (c) =>
  clean(c?.color) ||
  clean(c?.nombre) ||
  clean(c?.descripcion) ||
  clean(c?.label) ||
  "-";

const getCodigoColor = (c) =>
  clean(c?.codcolor) ||
  clean(c?.codigo) ||
  clean(c?.cod) ||
  "";

const TableProductos = () => {
  const puedeVerCostos = puedeVerCostosFront();

  const [productos, setProductos] = useState({
    data: { productos: [] },
    loading: true,
    error: "",
  });

  const [categorias, setCategorias] = useState({
    data: [],
    loading: true,
    error: "",
  });

  const [marcas, setMarcas] = useState({
    data: [],
    loading: true,
    error: "",
  });

  const [colores, setColores] = useState({
    data: [],
    loading: true,
    error: "",
  });

  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [show, setShow] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [busquedaProducto, setBusquedaProducto] = useState("");

  useEffect(() => {
    consultaTodo();
  }, []);

  const consultaTodo = async () => {
    try {
      const [datosProductos, datosCategorias, datosMarcas, datosColores] =
        await Promise.all([
          getProductos({ limit: 200, estado: "todos" }),
          getCategoriasProductos(),
          getMarcas(),
          getColores(),
        ]);

      const listadoProductos = [...unwrapProductos(datosProductos)];
      const totalProductos = Number(
        datosProductos?.total ?? listadoProductos.length
      );
      const limiteProductos = Number(datosProductos?.limit) || 200;

      let paginaProductos = 1;

      while (listadoProductos.length < totalProductos) {
        paginaProductos += 1;

        const respuesta = await getProductos({
          page: paginaProductos,
          limit: limiteProductos,
          estado: "todos",
        });

        const siguientes = unwrapProductos(respuesta);

        if (siguientes.length === 0) {
          throw new Error("No se pudo completar la carga de productos.");
        }

        listadoProductos.push(...siguientes);
      }

      setProductos({
        data: {
          productos: listadoProductos,
        },
        loading: false,
        error: "",
      });

      setCategorias({
        data: unwrapCategorias(datosCategorias),
        loading: false,
        error: "",
      });

      setMarcas({
        data: unwrapMarcas(datosMarcas),
        loading: false,
        error: "",
      });

      setColores({
        data: unwrapColores(datosColores),
        loading: false,
        error: "",
      });
    } catch (e) {
      console.error("❌ Error al cargar productos:", e);

      setProductos((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar productos",
      }));

      setCategorias((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar categorías",
      }));

      setMarcas((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar marcas",
      }));

      setColores((s) => ({
        ...s,
        loading: false,
        error: e?.message || "Error al cargar colores",
      }));
    }
  };

  const handleClose = () => {
    setShow(false);
    consultaTodo();
  };

  const handleShow = () => setShow(true);

  const modificaProducto = (producto) => {
    setProductoSeleccionado(producto);
    handleShow();
  };

  const eliminarProducto = (id) => {
    if (window.confirm("¿Está seguro que desea borrar el producto?")) {
      deleteProducto(id).then(() => consultaTodo());
    }
  };

  const categoriasMap = useMemo(() => {
    const map = {};

    categorias.data.forEach((c) => {
      const id = getId(c);
      const codigo = getCodigoCategoria(c);
      const nombre = getNombreCategoria(c);

      if (id) map[id] = nombre;
      if (codigo) map[String(codigo)] = nombre;
    });

    return map;
  }, [categorias.data]);

  const marcasMap = useMemo(() => {
    const map = {};

    marcas.data.forEach((m) => {
      const id = getId(m);
      const codigo = getCodigoMarca(m);
      const nombre = getNombreMarca(m);

      if (id) map[id] = nombre;
      if (codigo) map[String(codigo)] = nombre;
    });

    return map;
  }, [marcas.data]);

  const coloresMap = useMemo(() => {
    const map = {};

    colores.data.forEach((c) => {
      const id = getId(c);
      const codigo = getCodigoColor(c);
      const nombre = getNombreColor(c);

      if (id) map[id] = nombre;
      if (codigo) map[String(codigo)] = nombre;
    });

    return map;
  }, [colores.data]);

  if (
    productos.loading ||
    categorias.loading ||
    marcas.loading ||
    colores.loading
  ) {
    return <div className="p-3">Cargando productos…</div>;
  }

  if (productos.error) {
    return <div className="p-3 text-danger">{productos.error}</div>;
  }

  if (categorias.error) {
    return <div className="p-3 text-danger">{categorias.error}</div>;
  }

  if (marcas.error) {
    return <div className="p-3 text-danger">{marcas.error}</div>;
  }

  if (colores.error) {
    return <div className="p-3 text-danger">{colores.error}</div>;
  }

  const listaProductos = productos.data?.productos || [];

  const totalProductos = listaProductos.length;

  const totalActivos = listaProductos.filter(
    (producto) => producto.activo ?? true
  ).length;

  const totalInhabilitados = totalProductos - totalActivos;

  const normalizarBusqueda = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const textoBusqueda = normalizarBusqueda(busquedaProducto);

  const rows = (productos.data?.productos || [])
    .filter((p) => {
      if (filtroEstado === "todos") return true;

      const activo = p?.activo ?? true;
      return filtroEstado === "activos" ? activo : !activo;
    })

    .filter((p) => {
      if (!textoBusqueda) return true;

      return [
        p.nombreComercial,
        p.descripcion,
        p.modelo,
      ].some((valor) =>
        normalizarBusqueda(valor).includes(textoBusqueda)
      );
    })

    .map((p) => {
      const categoriaId = getId(p?.categoria);
      const marcaId = getId(p?.marca);

      const nombreCategoria =
        getNombreCategoria(p?.categoria) !== "-"
          ? getNombreCategoria(p?.categoria)
          : categoriasMap[categoriaId] || "-";

      const nombreMarca =
        getNombreMarca(p?.marca) !== "-"
          ? getNombreMarca(p?.marca)
          : marcasMap[marcaId] || "-";

      const coloresDisponibles = Array.isArray(p?.coloresDisponibles)
        ? p.coloresDisponibles
        : [];

      const nombresColores = coloresDisponibles
        .map((c) => {
          const id = getId(c);

          if (typeof c === "object") {
            const nombre = getNombreColor(c);
            if (nombre !== "-") return nombre;
          }

          return coloresMap[id] || id || "-";
        })
        .filter(Boolean)
        .join(", ");

      return {
        ...p,
        nombreCategoria,
        nombreMarca,
        nombresColores: nombresColores || "-",
      };
    })
    .sort((a, b) => {
      const A = a?.nombreComercial?.toString().trim() || "";
      const B = b?.nombreComercial?.toString().trim() || "";

      if (!A && !B) return 0;
      if (!A) return 1;
      if (!B) return -1;

      const cmpProducto = A.localeCompare(B, "es", {
        sensitivity: "base",
      });

      if (cmpProducto !== 0) return cmpProducto;

      return (a?.modelo || "").localeCompare(b?.modelo || "", "es", {
        sensitivity: "base",
      });
    });

  return (
    <div className="table-container">
      <div className="col-12 mt-4"></div>

      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <label htmlFor="productos-filtro-estado" className="mb-0">
          Estado
        </label>

        <select
          id="productos-filtro-estado"
          className="form-select form-select-sm"
          style={{ width: "auto", minWidth: 160 }}
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
        >
          <option value="todos">Todos</option>
          <option value="activos">Activos</option>
          <option value="inhabilitados">Inhabilitados</option>
        </select>

        <div style={{ flex: "1 1 280px", maxWidth: 460 }}>
          <input
            type="search"
            className="form-control form-control-sm"
            placeholder="Buscar por producto o modelo..."
            aria-label="Buscar producto o modelo"
            value={busquedaProducto}
            onChange={(e) => setBusquedaProducto(e.target.value)}
          />
        </div>

        <div className="d-flex align-items-center flex-wrap gap-2">
          <span className="badge bg-primary">
            Total: {totalProductos}
          </span>

          <span className="badge bg-success">
            Activos: {totalActivos}
          </span>

          <span className="badge bg-secondary">
            Inhabilitados: {totalInhabilitados}
          </span>

          <span className="small text-muted">
            Mostrando: {rows.length}
          </span>
        </div>
      </div>

      <Table striped bordered hover responsive className="tabla-productos-gestion">
        <thead>
          <tr className="header">
            <th className="borrar">Acciones</th>
            <th>Código</th>
            <th>Producto</th>
            <th>Modelo</th>
            <th>Categoría</th>
            <th>Marca</th>
            <th>Motor</th>
            <th>Color</th>
            <th>Precio Venta USD</th>

            {puedeVerCostos && (
              <th>Costo Producto USD</th>
            )}

            <th
              style={{
                width: "60px",
                minWidth: "60px",
                padding: "4px",
                textAlign: "center",
                whiteSpace: "nowrap",
              }}
            >
              Activo
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={puedeVerCostos ? 11 : 10}
                className="text-center"
              >
                No hay productos.
              </td>
            </tr>
          ) : (
            rows.map((producto) => (
              <tr key={producto._id}>
                <td className="borrar" style={{ whiteSpace: "nowrap" }}>
                  <div className="d-flex align-items-center justify-content-center gap-2 flex-nowrap">
                    <button
                      className="btn btn-primary"
                      onClick={() => modificaProducto(producto)}
                    >
                      <i className="fa fa-pencil-square-o" />
                    </button>

                    <button
                      className="btn btn-danger"
                      onClick={() => eliminarProducto(producto._id)}
                    >
                      <i className="fa fa-trash-o" />
                    </button>
                  </div>
                </td>

                <td>{producto.codprod || "-"}</td>
                <td>
                  {producto.nombreComercial || producto.descripcion || "-"}
                </td>
                <td>{producto.modelo || "-"}</td>
                <td>{producto.nombreCategoria}</td>
                <td>{producto.nombreMarca}</td>
                <td>{producto.tipoMotor || "-"}</td>
                <td>
                  {producto.usaColor ? producto.nombresColores : "NO APLICA"}
                </td>
                <td>{formatUSD(producto.precioVentaUSD)}</td>

                {puedeVerCostos && (
                  <td>
                    {formatUSD(producto.costoProductoUSD)}
                  </td>
                )}

                <td
                  style={{
                    width: "60px",
                    minWidth: "60px",
                    padding: "4px",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {(producto.activo ?? true) ? "Sí" : "No"}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      <ModalProducto
        show={show}
        handleClose={handleClose}
        producto={productoSeleccionado}
      />
    </div>
  );
};

export default TableProductos;